import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Printer, ArrowUpRight, Facebook, Linkedin, Instagram } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY, NAV_LINKS, SERVICES } from "@/data/site";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list — thank you.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-teh-deep text-white pt-20 pb-10 mt-0"
    >
      <div className="teh-container">
        {/* Top: CTA band */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-white/10">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-300">
              Newsletter
            </p>
            <h3 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">
              Stay close to the work
              <br />
              <span className="italic text-white/70">
                of caring for Georgia.
              </span>
            </h3>
            <p className="mt-5 text-white/60 max-w-xl">
              Quarterly notes on care innovation, workforce programs, and community impact —
              from the 1st Touch team.
            </p>
          </div>
          <form
            onSubmit={subscribe}
            className="lg:col-span-5 flex flex-col justify-center"
            data-testid="footer-newsletter-form"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="footer-newsletter-email"
                className="flex-1 px-5 py-4 bg-white/5 border border-white/15 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-emerald-300/60 transition"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-emerald-500 text-white text-sm font-medium rounded-full hover:bg-emerald-600 transition disabled:opacity-60"
              >
                {loading ? "Subscribing…" : "Subscribe"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-3 text-xs text-white/40">
              We respect inboxes. Unsubscribe any time.
            </p>
          </form>
        </div>

        {/* Middle: columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-16">
          <div className="col-span-2 md:col-span-1">
            <div className="font-serif text-2xl text-white mb-2">1st Touch</div>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-300 mb-5">
              Enterprise Holdings
            </p>
            <p className="mt-5 text-base text-white/85 leading-relaxed">
              {COMPANY.promise}
            </p>
            <div className="flex gap-3 mt-6">
              <a aria-label="LinkedIn" href="#" className="p-2 border border-white/15 rounded-full hover:bg-white/10 transition">
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a aria-label="Facebook" href="#" className="p-2 border border-white/15 rounded-full hover:bg-white/10 transition">
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a aria-label="Instagram" href="#" className="p-2 border border-white/15 rounded-full hover:bg-white/10 transition">
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-300 mb-5">Company</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/85 hover:text-white transition">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-300 mb-5">Services</p>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => {
                const linkProps = s.external_url
                  ? { href: s.external_url, target: "_blank", rel: "noopener noreferrer" }
                  : null;
                return (
                  <li key={s.slug}>
                    {linkProps ? (
                      <a {...linkProps} className="text-sm text-white/85 hover:text-white transition">
                        {s.name}
                      </a>
                    ) : (
                      <Link to={`/services/${s.slug}`} className="text-sm text-white/85 hover:text-white transition">
                        {s.name}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-emerald-300 mb-5">Contact</p>
            <ul className="space-y-4 text-sm text-white/85">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" strokeWidth={1.5} />
                <span>
                  {COMPANY.addressLine1}<br />
                  {COMPANY.addressLine2}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" strokeWidth={1.5} />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">{COMPANY.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Printer className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" strokeWidth={1.5} />
                <span>Fax: {COMPANY.fax}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">{COMPANY.email}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/70">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {COMPANY.certifications.map((c) => (
              <span key={c} className="text-xs text-white/70">{c}</span>
            ))}
            <span className="text-white/20">·</span>
            <Link to="/driver" data-testid="footer-driver-signin" className="text-xs text-white/70 hover:text-emerald-300 transition">
              Driver sign in
            </Link>
            <Link to="/dispatcher" data-testid="footer-dispatch-signin" className="text-xs text-white/70 hover:text-emerald-300 transition">
              Dispatch sign in
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
